import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Lock,
  Loader2,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  Unlock,
  UserPlus,
  X,
} from "lucide-react";
import { Toaster, toast } from "react-hot-toast";
import {
  assignPowerSplitterMeterOwner,
  createPowerSplitterMeter,
  queryPowerSplitterMeters,
  togglePowerSplitterMeterLockdown,
  updatePowerSplitterMeterAddress,
  updatePowerSplitterMeterName,
  verifyPowerSplitterMeterInManifest,
} from "../api/power-splitter-api";
import { useAuth } from "../context/authContext";
import { ComponentLoader } from "../components/general/loaders";
import { formatDateTime } from "../utils";

const EMPTY_FILTERS = {
  meterSn: "",
  centralMeterSn: "",
  owner: "",
  page: 1,
  limit: 20,
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_CREATE_METER_FORM = {
  meterSn: "",
  owner: "",
  address: "",
  name: "",
};

const getRows = (result) => {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.items)) return result.items;
  if (Array.isArray(result?.meters)) return result.meters;
  if (Array.isArray(result?.records)) return result.records;
  return [];
};

const getTotalPages = (result) => {
  const totalPages = result?.totalPages ?? result?.total_pages;
  if (totalPages) return totalPages;

  const total = result?.total ?? result?.count;
  const limit = result?.limit ?? EMPTY_FILTERS.limit;
  if (total) return Math.max(1, Math.ceil(total / limit));

  return 1;
};

const getTotalRecords = (result, meters) =>
  result?.total ?? result?.count ?? meters.length;

const hasOwner = (meter) => Boolean((meter?.owner || "").trim());

const getMeterRowKey = (meter, index) =>
  `${meter?.meterSn || meter?.centralMeterSn || "meter"}-${index}`;

export default function PowerSplitterMeters() {
  const { NETWORK, client } = useAuth();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [ownerModalOpen, setOwnerModalOpen] = useState(false);
  const [createMeterModalOpen, setCreateMeterModalOpen] = useState(false);
  const [lockdownConfirmModalOpen, setLockdownConfirmModalOpen] =
    useState(false);
  const [selectedMeter, setSelectedMeter] = useState(null);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [createMeterForm, setCreateMeterForm] = useState(
    EMPTY_CREATE_METER_FORM,
  );
  const [updatingAddress, setUpdatingAddress] = useState(false);
  const [updatingName, setUpdatingName] = useState(false);
  const [updatingOwner, setUpdatingOwner] = useState(false);
  const [creatingMeter, setCreatingMeter] = useState(false);
  const [togglingLockdownMeterSn, setTogglingLockdownMeterSn] = useState("");
  const [openActionMenuMeterSn, setOpenActionMenuMeterSn] = useState("");
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const actionMenuRef = useRef(null);

  const meters = useMemo(() => getRows(result), [result]);
  const openActionMenuMeter = useMemo(() => {
    if (!openActionMenuMeterSn) return null;

    return meters.find(
      (meter, index) => getMeterRowKey(meter, index) === openActionMenuMeterSn,
    );
  }, [meters, openActionMenuMeterSn]);
  const totalPages = getTotalPages(result);
  const totalRecords = getTotalRecords(result, meters);
  const trimmedName = name.trim();
  const trimmedAddress = address.trim();
  const trimmedOwnerEmail = ownerEmail.trim();
  const currentAddress = (selectedMeter?.address || "").trim();
  const currentName = (selectedMeter?.name || "").trim();
  const currentOwner = (selectedMeter?.owner || "").trim();
  const canSubmitName = Boolean(trimmedName) && trimmedName !== currentName;
  const canSubmitAddress =
    Boolean(trimmedAddress) && trimmedAddress !== currentAddress;
  const canSubmitOwner =
    Boolean(trimmedOwnerEmail) && trimmedOwnerEmail !== currentOwner;
  const canSubmitCreateMeter =
    Boolean(createMeterForm.meterSn.trim()) &&
    (!createMeterForm.owner.trim() ||
      EMAIL_PATTERN.test(createMeterForm.owner.trim()));

  // console.log('client',client)

  const fetchMeters = async (nextFilters = filters) => {
    if (!NETWORK) return;

    try {
      setLoading(true);
      setErrorMessage("");
      const data = await queryPowerSplitterMeters({
        ...nextFilters,
        provider: client.client_id,
      });
      setResult(data);
      setFilters(nextFilters);
    } catch (error) {
      console.error("Error fetching power splitter meters:", error);
      setErrorMessage("Unable to load power splitter meters right now.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeters(EMPTY_FILTERS);
  }, [NETWORK]);

  const closeActionMenu = useCallback(() => {
    setOpenActionMenuMeterSn("");
    setActionMenuPosition(null);
  }, []);

  useEffect(() => {
    if (!openActionMenuMeterSn) return undefined;

    const handleClickOutside = (event) => {
      if (event.target.closest("[data-meter-action-menu]")) return;
      if (actionMenuRef.current?.contains(event.target)) return;
      closeActionMenu();
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") closeActionMenu();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", closeActionMenu);
    window.addEventListener("scroll", closeActionMenu, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", closeActionMenu);
      window.removeEventListener("scroll", closeActionMenu, true);
    };
  }, [closeActionMenu, openActionMenuMeterSn]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    fetchMeters({ ...filters, page: 1 });
  };

  const handleClear = () => {
    fetchMeters(EMPTY_FILTERS);
  };

  const handlePageChange = (page) => {
    fetchMeters({ ...filters, page });
  };

  const toggleActionMenu = (rowKey, event) => {
    if (openActionMenuMeterSn === rowKey) {
      closeActionMenu();
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 176;
    const menuHeight = 160;
    const left = Math.min(
      window.innerWidth - menuWidth - 8,
      Math.max(8, rect.right - menuWidth),
    );
    const opensUp = rect.bottom + menuHeight + 8 > window.innerHeight;
    const top = opensUp
      ? Math.max(8, rect.top - menuHeight - 6)
      : rect.bottom + 6;

    setActionMenuPosition({ left, top });
    setOpenActionMenuMeterSn(rowKey);
  };

  const openCreateMeterModal = () => {
    setCreateMeterForm(EMPTY_CREATE_METER_FORM);
    setCreateMeterModalOpen(true);
  };

  const closeCreateMeterModal = (force = false) => {
    if (creatingMeter && !force) return;

    setCreateMeterModalOpen(false);
    setCreateMeterForm(EMPTY_CREATE_METER_FORM);
  };

  const handleCreateMeterChange = (field, value) => {
    setCreateMeterForm((prev) => ({ ...prev, [field]: value }));
  };

  const openNameModal = (meter) => {
    closeActionMenu();
    setSelectedMeter(meter);
    setName(meter?.name || "");
    setNameModalOpen(true);
  };

  const closeNameModal = (force = false) => {
    if (updatingName && !force) return;

    setNameModalOpen(false);
    setSelectedMeter(null);
    setName("");
  };

  const openAddressModal = (meter) => {
    closeActionMenu();
    setSelectedMeter(meter);
    setAddress(meter?.address || "");
    setAddressModalOpen(true);
  };

  const closeAddressModal = (force = false) => {
    if (updatingAddress && !force) return;

    setAddressModalOpen(false);
    setSelectedMeter(null);
    setAddress("");
  };

  const openOwnerModal = (meter) => {
    closeActionMenu();
    setSelectedMeter(meter);
    setOwnerEmail(meter?.owner || "");
    setOwnerModalOpen(true);
  };

  const closeOwnerModal = (force = false) => {
    if (updatingOwner && !force) return;

    setOwnerModalOpen(false);
    setSelectedMeter(null);
    setOwnerEmail("");
  };

  const openLockdownConfirmModal = (meter) => {
    closeActionMenu();
    setSelectedMeter(meter);
    setLockdownConfirmModalOpen(true);
  };

  const closeLockdownConfirmModal = (force = false) => {
    if (togglingLockdownMeterSn && !force) return;

    setLockdownConfirmModalOpen(false);
    setSelectedMeter(null);
  };

  const handleUpdateName = async (event) => {
    event.preventDefault();

    const nextName = trimmedName;
    const meterSn = selectedMeter?.meterSn;

    if (!meterSn) {
      toast.error("Meter serial number is missing.", { duration: 5000 });
      return;
    }

    if (!nextName) {
      toast.error("Please enter a meter name.", { duration: 5000 });
      return;
    }

    if (nextName === currentName) {
      toast.error("Please change the meter name before updating.", {
        duration: 5000,
      });
      return;
    }

    try {
      setUpdatingName(true);
      await updatePowerSplitterMeterName(meterSn, nextName);
      toast.success("Meter name updated successfully.", { duration: 5000 });
      closeNameModal(true);
      await fetchMeters(filters);
    } catch (error) {
      console.error("Error updating power splitter meter name:", error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to update meter name.";
      toast.error(message, { duration: 5000 });
    } finally {
      setUpdatingName(false);
    }
  };

  const handleUpdateAddress = async (event) => {
    event.preventDefault();

    const nextAddress = trimmedAddress;
    const meterSn = selectedMeter?.meterSn;

    if (!meterSn) {
      toast.error("Meter serial number is missing.", { duration: 5000 });
      return;
    }

    if (!nextAddress) {
      toast.error("Please enter a meter address.", { duration: 5000 });
      return;
    }

    if (nextAddress === currentAddress) {
      toast.error("Please change the meter address before updating.", {
        duration: 5000,
      });
      return;
    }

    try {
      setUpdatingAddress(true);
      await updatePowerSplitterMeterAddress(meterSn, nextAddress);
      toast.success("Meter address updated successfully.", { duration: 5000 });
      closeAddressModal(true);
      await fetchMeters(filters);
    } catch (error) {
      console.error("Error updating power splitter meter address:", error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to update meter address.";
      toast.error(message, { duration: 5000 });
    } finally {
      setUpdatingAddress(false);
    }
  };

  const handleToggleLockdown = async () => {
    const meterSn = selectedMeter?.meterSn;

    if (!meterSn) {
      toast.error("Meter serial number is missing.", { duration: 5000 });
      return;
    }

    const nextLockdownState = selectedMeter?.isLockedDown !== true;

    try {
      setTogglingLockdownMeterSn(meterSn);
      await togglePowerSplitterMeterLockdown(meterSn, nextLockdownState);
      toast.success(
        `Meter access ${nextLockdownState ? "disabled" : "enabled"} successfully.`,
        { duration: 5000 },
      );
      closeLockdownConfirmModal(true);
      await fetchMeters(filters);
    } catch (error) {
      console.error("Error toggling power splitter meter lockdown:", error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to update meter lockdown status.";
      toast.error(message, { duration: 5000 });
    } finally {
      setTogglingLockdownMeterSn("");
    }
  };

  const handleAssignOwner = async (event) => {
    event.preventDefault();

    const nextOwner = trimmedOwnerEmail;
    const meterSn = selectedMeter?.meterSn;
    const hasCurrentOwner = Boolean(currentOwner);

    if (!meterSn) {
      toast.error("Meter serial number is missing.", { duration: 5000 });
      return;
    }

    if (!nextOwner) {
      toast.error("Please enter an owner email.", { duration: 5000 });
      return;
    }

    if (!EMAIL_PATTERN.test(nextOwner)) {
      toast.error("Please enter a valid owner email.", { duration: 5000 });
      return;
    }

    if (nextOwner === currentOwner) {
      toast.error("Please change the owner email before updating.", {
        duration: 5000,
      });
      return;
    }

    try {
      setUpdatingOwner(true);
      await assignPowerSplitterMeterOwner(meterSn, nextOwner);
      toast.success(
        `Meter owner ${hasCurrentOwner ? "changed" : "assigned"} successfully.`,
        { duration: 5000 },
      );
      closeOwnerModal(true);
      await fetchMeters(filters);
    } catch (error) {
      console.error("Error assigning power splitter meter owner:", error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to update meter owner.";
      toast.error(message, { duration: 5000 });
    } finally {
      setUpdatingOwner(false);
    }
  };

  const handleCreateMeter = async (event) => {
    event.preventDefault();

    const meterSn = createMeterForm.meterSn.trim();
    const owner = createMeterForm.owner.trim();
    const address = createMeterForm.address.trim();
    const provider = client?.client_id || "";
    const name = createMeterForm.name.trim();

    if (!meterSn) {
      toast.error("Please enter a meter serial number.", { duration: 5000 });
      return;
    }

    if (owner && !EMAIL_PATTERN.test(owner)) {
      toast.error("Please enter a valid owner email.", { duration: 5000 });
      return;
    }

    if (!provider) {
      toast.error("Provider is missing.", { duration: 5000 });
      return;
    }

    try {
      setCreatingMeter(true);
      const manifestResult = await verifyPowerSplitterMeterInManifest(meterSn);
      const manifestMeter = manifestResult?.data || manifestResult;

      if (manifestMeter?.testStatus !== "PASSED") {
        toast.error(
          "This meter has not passed manifest verification. Please contact support.",
          { duration: 5000 },
        );
        return;
      }

      await createPowerSplitterMeter({
        meterSn,
        provider,
        ...(name && { name }),
        ...(owner && { owner }),
        ...(address && { address }),
      });
      toast.success("Power splitter meter created successfully.", {
        duration: 5000,
      });
      closeCreateMeterModal(true);
      await fetchMeters({ ...filters, page: 1 });
    } catch (error) {
      console.error("Error verifying or creating power splitter meter:", error);
      if (error?.response?.status === 404) {
        toast.error(
          "This meter was not found in the manifest. Please contact support.",
          { duration: 5000 },
        );
        return;
      }

      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to verify or create power splitter meter.";
      toast.error(message, { duration: 5000 });
    } finally {
      setCreatingMeter(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Toaster position="top-center" />
      <section className="rounded-2xl bg-white p-4 font-nexa">
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            onClick={openCreateMeterModal}
            className="flex h-10 items-center justify-center gap-2 rounded-lg bg-primary-dark px-4 text-sm font-bold text-primary-light hover:bg-primary-title"
          >
            <Plus size={16} />
            Create Meter
          </button>
        </div>
        <div className="grid gap-3 md:grid-cols-4">
          <FilterInput
            label="Meter SN"
            value={filters.meterSn}
            onChange={(value) => handleFilterChange("meterSn", value)}
            onEnter={handleSearch}
          />
          {/* <FilterInput
            label="Central Meter SN"
            value={filters.centralMeterSn}
            onChange={(value) => handleFilterChange("centralMeterSn", value)}
            onEnter={handleSearch}
          /> */}
          <FilterInput
            label="Owner"
            value={filters.owner}
            onChange={(value) => handleFilterChange("owner", value)}
            onEnter={handleSearch}
          />
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={handleSearch}
              className="flex h-10 flex-1 items-center justify-center gap-2 rounded-lg bg-primary-dark px-3 text-sm font-bold text-primary-light hover:bg-primary-title"
            >
              <Search size={16} />
              Search
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="h-10 rounded-lg border border-primary-light px-3 text-sm font-bold text-primary-dark hover:bg-primary-light"
            >
              Clear
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-2xl bg-white p-4 font-nexa">
        {loading && (
          <div className="flex min-h-56 items-center justify-center">
            <ComponentLoader />
          </div>
        )}

        {!loading && errorMessage && (
          <div className="py-8 text-center text-sm font-bold text-error">
            {errorMessage}
          </div>
        )}

        {!loading && !errorMessage && meters.length === 0 && (
          <div className="py-12 text-center text-sm font-bold text-primary-title">
            No power splitter meters found.
          </div>
        )}

        {!loading && !errorMessage && meters.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-[1240px] w-full table-auto">
                <thead>
                  <tr className="bg-primary-light text-xs uppercase text-primary-title">
                    <th className="px-4 py-3 text-left">Meter SN</th>
                    <th className="px-4 py-3 text-left">Name</th>
                    {/* <th className="px-4 py-3 text-left">Central Meter SN</th> */}
                    {/* <th className="px-4 py-3 text-left">IP Address</th> */}
                    {/* <th className="px-4 py-3 text-left">Port</th> */}
                    <th className="px-4 py-3 text-left">Owner</th>
                    <th className="px-4 py-3 text-left">Address</th>
                    <th className="px-4 py-3 text-left">Meter Access</th>
                    <th className="px-4 py-3 text-left">Setup Status</th>
                    {/* <th className="px-4 py-3 text-left">ModBus</th> */}
                    <th className="px-4 py-3 text-left">Date Enrolled</th>
                    <th className="px-4 py-3 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {meters.map((meter, index) => {
                    const rowKey = getMeterRowKey(meter, index);

                    return (
                      <tr
                        key={rowKey}
                        className="border-b border-primary-light text-sm text-primary-dark"
                      >
                        <td className="px-4 py-3 font-bold">
                          {meter.meterSn || "-"}
                        </td>
                        <td className="px-4 py-3">{meter.name || "-"}</td>
                        {/* <td className="px-4 py-3">{meter.centralMeterSn || "-"}</td> */}
                        {/* <td className="px-4 py-3">{meter.ipAddress || "-"}</td> */}
                        {/* <td className="px-4 py-3">{meter.port ?? "-"}</td> */}
                        <td className="px-4 py-3">{meter.owner || "-"}</td>
                        <td className="px-4 py-3 max-w-[260px] whitespace-normal">
                          {meter.address || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <MeterAccessBadge value={meter.isLockedDown} />
                        </td>
                        <td className="px-4 py-3">
                          <SetupStatusBadge value={meter.isActivated} />
                        </td>
                        {/* <td className="px-4 py-3">{meter.modBusSlaveAddress ?? "-"}</td> */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          {meter.createdAt
                            ? formatDateTime(meter.createdAt)
                            : "-"}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            data-meter-action-menu
                            onClick={(event) => toggleActionMenu(rowKey, event)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-primary-light text-primary-dark hover:bg-primary-light"
                            title="Meter actions"
                          >
                            <MoreVertical size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-primary-title mb-0">
                Page {filters.page} of {totalPages} - {totalRecords} records
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handlePageChange(filters.page - 1)}
                  disabled={filters.page <= 1}
                  className="rounded-lg border border-primary-light px-3 py-2 text-sm font-bold text-primary-dark disabled:cursor-not-allowed disabled:opacity-50 hover:bg-primary-light"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => handlePageChange(filters.page + 1)}
                  disabled={filters.page >= totalPages}
                  className="rounded-lg border border-primary-light px-3 py-2 text-sm font-bold text-primary-dark disabled:cursor-not-allowed disabled:opacity-50 hover:bg-primary-light"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      {openActionMenuMeter && actionMenuPosition && (
        <div
          ref={actionMenuRef}
          data-meter-action-menu
          className="fixed z-[60] w-44 rounded-lg border border-primary-light bg-white py-1 font-nexa shadow-lg"
          style={{
            left: `${actionMenuPosition.left}px`,
            top: `${actionMenuPosition.top}px`,
          }}
        >
          <button
            type="button"
            onClick={() => openNameModal(openActionMenuMeter)}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-bold text-primary-dark hover:bg-primary-light"
          >
            <Pencil size={14} />
            Update name
          </button>
          <button
            type="button"
            onClick={() => openAddressModal(openActionMenuMeter)}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-bold text-primary-dark hover:bg-primary-light"
          >
            <Pencil size={14} />
            Update address
          </button>
          <button
            type="button"
            onClick={() => openOwnerModal(openActionMenuMeter)}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-bold text-primary-dark hover:bg-primary-light"
          >
            <UserPlus size={14} />
            {hasOwner(openActionMenuMeter) ? "Change Owner" : "Assign Owner"}
          </button>
          <button
            type="button"
            onClick={() => openLockdownConfirmModal(openActionMenuMeter)}
            disabled={togglingLockdownMeterSn === openActionMenuMeter.meterSn}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-bold text-primary-dark hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
          >
            {togglingLockdownMeterSn === openActionMenuMeter.meterSn ? (
              <Loader2 className="animate-spin" size={14} />
            ) : openActionMenuMeter.isLockedDown ? (
              <Unlock size={14} />
            ) : (
              <Lock size={14} />
            )}
            {openActionMenuMeter.isLockedDown
              ? "Enable Meter Access"
              : "Disable Meter Access"}
          </button>
        </div>
      )}

      {nameModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 font-nexa">
          <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h2 className="mb-1 text-lg font-extrabold text-primary-dark">
                  Update meter name
                </h2>
                <p className="mb-0 text-sm text-subText">
                  Meter SN: {selectedMeter?.meterSn || "-"}
                </p>
              </div>
              <button
                type="button"
                onClick={closeNameModal}
                disabled={updatingName}
                className="flex h-8 w-8 items-center justify-center rounded-full text-primary-title hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleUpdateName} className="flex flex-col gap-3">
              <TextField
                label="Name"
                value={name}
                onChange={setName}
                disabled={updatingName}
              />

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeNameModal}
                  disabled={updatingName}
                  className="rounded-lg border border-primary-light px-4 py-2 text-sm font-bold text-primary-dark hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updatingName || !canSubmitName}
                  className="flex items-center justify-center gap-2 rounded-lg bg-primary-dark px-4 py-2 text-sm font-bold text-primary-light hover:bg-primary-title disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {updatingName && (
                    <Loader2 className="animate-spin" size={16} />
                  )}
                  Update name
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {lockdownConfirmModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 font-nexa">
          <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h2 className="mb-1 text-lg font-extrabold text-primary-dark">
                  {selectedMeter?.isLockedDown
                    ? "Enable Meter Access"
                    : "Disable Meter Access"}
                </h2>
                <p className="mb-0 text-sm text-subText">
                  Meter SN: {selectedMeter?.meterSn || "-"}
                </p>
              </div>
              <button
                type="button"
                onClick={closeLockdownConfirmModal}
                disabled={Boolean(togglingLockdownMeterSn)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-primary-title hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            <p className="mb-4 text-sm text-primary-dark">
              Are you sure you want to{" "}
              {selectedMeter?.isLockedDown ? "enable" : "disable"} meter access?
            </p>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeLockdownConfirmModal}
                disabled={Boolean(togglingLockdownMeterSn)}
                className="rounded-lg border border-primary-light px-4 py-2 text-sm font-bold text-primary-dark hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleToggleLockdown}
                disabled={Boolean(togglingLockdownMeterSn)}
                className={`flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50 ${
                  selectedMeter?.isLockedDown
                    ? "bg-primary-dark hover:bg-primary-title"
                    : "bg-error hover:bg-error"
                }`}
              >
                {togglingLockdownMeterSn && (
                  <Loader2 className="animate-spin" size={16} />
                )}
                {selectedMeter?.isLockedDown
                  ? "Enable Meter Access"
                  : "Disable Meter Access"}
              </button>
            </div>
          </div>
        </div>
      )}

      {createMeterModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 font-nexa">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h2 className="mb-1 text-lg font-extrabold text-primary-dark">
                  Enroll Meter
                </h2>
              </div>
              <button
                type="button"
                onClick={closeCreateMeterModal}
                disabled={creatingMeter}
                className="flex h-8 w-8 items-center justify-center rounded-full text-primary-title hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateMeter} className="flex flex-col gap-4">
              <div className="grid gap-3">
                <TextField
                  label="Meter SN"
                  value={createMeterForm.meterSn}
                  onChange={(value) =>
                    handleCreateMeterChange("meterSn", value)
                  }
                  disabled={creatingMeter}
                />
                <TextField
                  label="Name"
                  value={createMeterForm.name}
                  onChange={(value) => handleCreateMeterChange("name", value)}
                  disabled={creatingMeter}
                />
                <TextField
                  label="Owner email"
                  type="email"
                  value={createMeterForm.owner}
                  onChange={(value) => handleCreateMeterChange("owner", value)}
                  disabled={creatingMeter}
                />
              </div>

              <label className="flex flex-col gap-1">
                <span className="text-xs font-bold uppercase tracking-2 text-subText">
                  Address
                </span>
                <textarea
                  value={createMeterForm.address}
                  onChange={(event) =>
                    handleCreateMeterChange("address", event.target.value)
                  }
                  rows={3}
                  disabled={creatingMeter}
                  className="rounded-lg border border-primary-light px-3 py-2 text-sm text-primary-dark outline-none focus:border-primary-normal disabled:cursor-not-allowed disabled:opacity-60"
                />
              </label>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeCreateMeterModal}
                  disabled={creatingMeter}
                  className="rounded-lg border border-primary-light px-4 py-2 text-sm font-bold text-primary-dark hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingMeter || !canSubmitCreateMeter}
                  className="flex items-center justify-center gap-2 rounded-lg bg-primary-dark px-4 py-2 text-sm font-bold text-primary-light hover:bg-primary-title disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {creatingMeter && (
                    <Loader2 className="animate-spin" size={16} />
                  )}
                  Create Meter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {addressModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 font-nexa">
          <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h2 className="mb-1 text-lg font-extrabold text-primary-dark">
                  Update meter address
                </h2>
                <p className="mb-0 text-sm text-subText">
                  Meter SN: {selectedMeter?.meterSn || "-"}
                </p>
              </div>
              <button
                type="button"
                onClick={closeAddressModal}
                disabled={updatingAddress}
                className="flex h-8 w-8 items-center justify-center rounded-full text-primary-title hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            <form
              onSubmit={handleUpdateAddress}
              className="flex flex-col gap-3"
            >
              <label className="flex flex-col gap-1">
                <span className="text-xs font-bold uppercase tracking-2 text-subText">
                  Address
                </span>
                <textarea
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                  rows={3}
                  disabled={updatingAddress}
                  className="rounded-lg border border-primary-light px-3 py-2 text-sm text-primary-dark outline-none focus:border-primary-normal disabled:cursor-not-allowed disabled:opacity-60"
                />
              </label>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeAddressModal}
                  disabled={updatingAddress}
                  className="rounded-lg border border-primary-light px-4 py-2 text-sm font-bold text-primary-dark hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updatingAddress || !canSubmitAddress}
                  className="flex items-center justify-center gap-2 rounded-lg bg-primary-dark px-4 py-2 text-sm font-bold text-primary-light hover:bg-primary-title disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {updatingAddress && (
                    <Loader2 className="animate-spin" size={16} />
                  )}
                  Update address
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {ownerModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 font-nexa">
          <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h2 className="mb-1 text-lg font-extrabold text-primary-dark">
                  {currentOwner ? "Change meter owner" : "Assign meter owner"}
                </h2>
                <p className="mb-0 text-sm text-subText">
                  Meter SN: {selectedMeter?.meterSn || "-"}
                </p>
              </div>
              <button
                type="button"
                onClick={closeOwnerModal}
                disabled={updatingOwner}
                className="flex h-8 w-8 items-center justify-center rounded-full text-primary-title hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleAssignOwner} className="flex flex-col gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-bold uppercase tracking-2 text-subText">
                  Owner email
                </span>
                <input
                  type="email"
                  value={ownerEmail}
                  onChange={(event) => setOwnerEmail(event.target.value)}
                  disabled={updatingOwner}
                  className="h-10 rounded-lg border border-primary-light px-3 text-sm text-primary-dark outline-none focus:border-primary-normal disabled:cursor-not-allowed disabled:opacity-60"
                />
              </label>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeOwnerModal}
                  disabled={updatingOwner}
                  className="rounded-lg border border-primary-light px-4 py-2 text-sm font-bold text-primary-dark hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updatingOwner || !canSubmitOwner}
                  className="flex items-center justify-center gap-2 rounded-lg bg-primary-dark px-4 py-2 text-sm font-bold text-primary-light hover:bg-primary-title disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {updatingOwner && (
                    <Loader2 className="animate-spin" size={16} />
                  )}
                  {currentOwner ? "Change Owner" : "Assign Owner"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterInput({ label, value, onChange, onEnter }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-bold uppercase tracking-2 text-subText">
        {label}
      </span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => event.key === "Enter" && onEnter()}
        className="h-10 rounded-lg border border-primary-light px-3 text-sm text-primary-dark outline-none focus:border-primary-normal"
      />
    </label>
  );
}

function TextField({ label, value, onChange, disabled, type = "text" }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-bold uppercase tracking-2 text-subText">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        className="h-10 rounded-lg border border-primary-light px-3 text-sm text-primary-dark outline-none focus:border-primary-normal disabled:cursor-not-allowed disabled:opacity-60"
      />
    </label>
  );
}

function MeterAccessBadge({ value }) {
  return (
    <span
      className={`inline-flex h-7 items-center rounded-full px-3 text-xs font-bold ${
        value ? "bg-error/10 text-error" : "bg-success/10 text-success"
      }`}
    >
      {value ? "Disabled" : "Enabled"}
    </span>
  );
}

function SetupStatusBadge({ value }) {
  return (
    <span
      className={`inline-flex h-7 items-center rounded-full px-3 text-xs font-bold ${
        value ? "bg-success/10 text-success" : "bg-error/10 text-error"
      }`}
    >
      {value ? "Completed" : "Uncompleted"}
    </span>
  );
}
